import React, { useEffect } from 'react';
import { getProxiedImageUrl } from '../utils/imageUtils';

interface ImagePreloaderProps {
  imageUrls: string[];
  quality?: number;
  width?: number;
  format?: 'webp' | 'avif' | 'auto';
}

/**
 * Component that preloads images to improve LCP and general page performance
 * This component doesn't render anything visible but adds preload links to the document head
 */
export function ImagePreloader({ 
  imageUrls, 
  quality = 90, 
  width = 500,
  format = 'auto'
}: ImagePreloaderProps) {
  useEffect(() => {
    // Skip preloading if the browser doesn't support it or if the user has 
    // the Save-Data header (indicates user wants to save data)
    const navigator = window.navigator as any;
    const connection = navigator.connection;
    const saveData = connection?.saveData || 
                    navigator.saveData || 
                    false;
    
    if (saveData) {
      console.log('Save-Data mode detected. Skipping image preloading.');
      return;
    }
    
    // Array to keep track of created elements for cleanup
    const preloadLinks: HTMLLinkElement[] = [];
    
    // Create preload links for each image
    imageUrls.forEach(url => {
      if (!url) return;
      
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = getProxiedImageUrl(url, quality, width, format);
      preloadLink.crossOrigin = 'anonymous';
      
      // Add fetchpriority if supported (newer browsers)
      if ('fetchpriority' in HTMLImageElement.prototype) {
        preloadLink.setAttribute('fetchpriority', 'high');
      }
      
      document.head.appendChild(preloadLink);
      preloadLinks.push(preloadLink);
    });
    
    // Clean up function to remove preload links when component unmounts
    return () => {
      preloadLinks.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [imageUrls, quality, width, format]);
  
  // This component doesn't render anything visible
  return null;
} 